import { useState } from 'react';
import { useNavigate } from 'react-router';
import { RotateCcw, Trash2 } from 'lucide-react';
import { api, type Page, type Schema } from '../api/client';
import { useStudio } from './App';
import { Dialog, Spinner, useApi } from './shared';

export function DeleteSeriesDialog({
  series,
  close,
}: {
  series: Schema<'Series'>;
  close: () => void;
}) {
  const { base, refresh, toast } = useStudio();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function remove() {
    setBusy(true);
    setError('');
    try {
      await api(`${base}/series/${series.id}`, 'DELETE', {
        expectedRevision: series.revision,
        confirmationTitle: series.title.trim(),
        stopActiveTasks: true,
      });
      sessionStorage.removeItem('dream.branch.' + series.id);
      await refresh();
      toast('系列已移入回收站，排队任务和后续流程已停止');
      close();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog
      title={`删除「${series.title}」`}
      close={() => {
        if (!busy) close();
      }}
    >
      <div className="dialog-body">
        <p>
          系列及其剧集、角色、素材和小说改编记录将一起移入回收站，小说流程会暂停。内容仍可从回收站恢复。
        </p>
        <p className="quiet">
          删除会取消排队任务并停止后续流程。已经发给模型的请求可能继续完成，原请求与回执会保留，恢复系列后可核对结果。
        </p>
        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="dialog-actions">
        <button autoFocus className="button secondary" disabled={busy} onClick={close}>
          取消
        </button>
        <button className="button danger" disabled={busy} onClick={() => void remove()}>
          <Trash2 size={16} />
          {busy ? '正在删除…' : '停止任务并删除系列'}
        </button>
      </div>
    </Dialog>
  );
}

export function SeriesTrashDialog({ close, canEdit }: { close: () => void; canEdit: boolean }) {
  const { base, refresh, toast, workspaceId } = useStudio();
  const query = useApi<Page<Schema<'Series'>>>(`${base}/series/trash`);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(''),
    [error, setError] = useState('');
  async function restore(series: Schema<'Series'>) {
    setBusy(series.id);
    setError('');
    try {
      await api(`${base}/series/${series.id}/restore`, 'POST', {
        expectedRevision: series.revision,
      });
      await refresh();
      toast('系列已恢复；已停止的任务不会自动重启，小说流程需手动继续');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  }
  return (
    <Dialog
      title="系列回收站"
      subtitle="恢复后保留原内容和素材，已停止的任务不会自动重启，小说流程需手动继续。"
      close={close}
    >
      <div className="dialog-body">
        {error && (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
        {query.isPending ? (
          <Spinner />
        ) : query.isError ? (
          <p className="error-banner">
            回收站读取失败。<button onClick={() => void query.refetch()}>重试</button>
          </p>
        ) : !query.data?.items.length ? (
          <p className="quiet">回收站是空的。</p>
        ) : (
          <div className="series-trash-list">
            {query.data.items.map((series) => (
              <div className="series-trash-row" key={series.id}>
                <div>
                  <strong>{series.title}</strong>
                  <small>
                    {series.deletedAt
                      ? new Date(series.deletedAt).toLocaleString('zh-CN') + ' 删除'
                      : ''}
                  </small>
                </div>
                <button
                  className="button secondary"
                  disabled={!canEdit || !!busy}
                  onClick={() => void restore(series)}
                >
                  <RotateCcw size={16} />
                  {busy === series.id ? '正在恢复…' : '恢复系列'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="dialog-actions">
        <button
          className="button secondary"
          onClick={() => {
            close();
            navigate(`/w/${workspaceId}/projects`);
          }}
        >
          返回系列列表
        </button>
      </div>
    </Dialog>
  );
}
